import { isValidEPAId } from "encamp-compliance-engine/src/schemas/rcra/epaId";
import { safeParseFloat } from "encamp-shared/src/utils/numberParsing/safeParseNumber";

/**
 * react-hook-form `validate` rules for the Filing Notifications sub-record
 * dialogs. They flag only FORMAT: an empty value always passes, because
 * completeness is the compliance engine's job, while a value that can't
 * round-trip — a non-numeric quantity that `safeParseFloat` would silently
 * turn into null, or a malformed EPA ID — shows an inline error at the field.
 * Advisory, like the waste-code chips: the dialog's Save never blocks (see
 * `SubRecordDialog`), and the step's debounced compliance validation surfaces
 * the persisted-data issues after commit.
 */

// The rules take `unknown` (narrowed inside) because react-hook-form types a
// validate callback's value as the union of ALL field value types on the form,
// which differs per dialog form.

export function validateOptionalPositiveNumber(value: unknown): true | string {
  const message = "Enter a positive number";
  if (typeof value === "number") {
    return value > 0 || message;
  }
  if (typeof value !== "string" || value.trim() === "") {
    return true;
  }
  const parsed = safeParseFloat(value);
  return (parsed != null && parsed > 0) || message;
}

/**
 * For fields persisted as integers (e.g. §16's `estimatedShortTons`, an Int in
 * the schema): a fractional value would parse fine here but be truncated on
 * save, so the user would see a "valid" field whose saved value silently
 * differs. Flag it instead.
 */
export function validateOptionalPositiveWholeNumber(value: unknown): true | string {
  const message = "Enter a positive whole number";
  if (typeof value === "number") {
    return (Number.isInteger(value) && value > 0) || message;
  }
  if (typeof value !== "string" || value.trim() === "") {
    return true;
  }
  const parsed = safeParseFloat(value);
  return (parsed != null && parsed > 0 && Number.isInteger(parsed)) || message;
}

/** Format per `isValidEPAId`: 2-letter state/territory prefix + 10 alphanumerics. */
export function validateOptionalEpaId(value: unknown): true | string {
  if (typeof value !== "string" || value.trim() === "") {
    return true;
  }
  return isValidEPAId(value) || "Must be a 12-character EPA ID (e.g. TXD982556581)";
}
